import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Take scroll control from the browser. We implement the correct semantics
// ourselves: scroll-to-top on FORWARD navigation, restore on BACK/FORWARD.
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

/**
 * ScrollToTop — correct scroll behavior across client-side navigation.
 *
 *  - FORWARD nav (clicking a link → history PUSH/REPLACE): scroll to TOP, like
 *    opening a fresh page.
 *  - BACK / FORWARD button (history POP): RESTORE the scroll position the user
 *    had on that page, like a normal browser back button.
 *
 * We disable native scrollRestoration (it fought our reset and was unreliable
 * with React's async render) and manage positions ourselves: a Map keyed by
 * each history entry's `location.key`. We save the outgoing page's scrollY in
 * the effect cleanup (which runs while the old page's scroll is still current),
 * and on POP we restore the saved value for the entry being returned to.
 *
 * Keyed on full `location` (not just pathname) but ignores pure hash changes
 * for the top-scroll, so in-page anchor jumps ("See how it works") aren't
 * hijacked.
 */
const getScroll = () =>
  window.scrollY
  || document.scrollingElement?.scrollTop
  || document.documentElement.scrollTop
  || 0;

const setScroll = (y) => {
  window.scrollTo(0, y);
  if (document.scrollingElement) document.scrollingElement.scrollTop = y;
  if (document.documentElement) document.documentElement.scrollTop = y;
  if (document.body) document.body.scrollTop = y;
  document.querySelectorAll('.global-main-content, .marketing-shell__content').forEach((el) => {
    el.scrollTop = y;
  });
};

const ScrollToTop = () => {
  const location = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'
  const positions = useRef(new Map());
  const prevKey = useRef(location.key);

  useEffect(() => {
    if (navType === 'POP') {
      // Returning to a prior entry — restore its saved scroll (default top).
      const saved = positions.current.get(location.key) ?? 0;
      setScroll(saved);
      const raf = requestAnimationFrame(() => setScroll(saved));
      const t = setTimeout(() => setScroll(saved), 80);
      prevKey.current = location.key;
      return () => { cancelAnimationFrame(raf); clearTimeout(t); };
    }

    // Forward navigation (PUSH/REPLACE) — fresh page, go to top. Re-assert
    // after paint so a late-mounting page can't leave a stale position.
    // EXCEPTION: a destination carrying a #hash owns its own scroll — the
    // target page's anchor-scroll effect handles it. Resetting here raced
    // that effect and won (our 80ms re-assert vs its scroll), so anchor
    // links from OTHER pages always landed at the top.
    if (location.hash) {
      prevKey.current = location.key;
      return undefined;
    }
    // A page that rewrites only its OWN query (Delegate's ?agent= / ?way=, Oct 2 2026) owns its scroll: it passes
    // state { keepScroll: true } and we leave the position alone. Opt-in, so every other navigation still tops out.
    if (location.state && location.state.keepScroll) {
      prevKey.current = location.key;
      return undefined;
    }
    setScroll(0);
    const raf = requestAnimationFrame(() => setScroll(0));
    const t = setTimeout(() => setScroll(0), 80);
    prevKey.current = location.key;
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [location.key, location.hash, location.state, navType]);

  // Continuously record the current page's scroll so a back navigation away
  // from it can be restored. Keyed by the live history entry.
  useEffect(() => {
    // Copy the ref's current Map for the cleanup (react-hooks/exhaustive-deps);
    // `positions` is a stable Map ref, never re-assigned, so this is safe.
    const positionMap = positions.current;
    const onScroll = () => { positionMap.set(location.key, getScroll()); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      // Capture the final position right before this entry is navigated away.
      positionMap.set(location.key, getScroll());
      window.removeEventListener('scroll', onScroll);
    };
  }, [location.key]);

  return null;
};

export default ScrollToTop;
